import { collection, deleteDoc, doc, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';
import type { OcdInfo } from './ocdProfileTypes';

/**
 * The server-side ceiling is Cloud Run's 300s request timeout, below the 540s the callable
 * ran with; keeping the client deadline generous costs nothing and leaves room if that
 * ceiling is ever raised.
 */
const GENERATE_PROFILE_TIMEOUT_MS = 540_000;

const generateOcdProfileCall = createApiCall<
  { ocdThemes: string[]; questJourneyDay: number; extraGuidance: string },
  unknown
>('adminGenerateOcdProfile', { timeoutMs: GENERATE_PROFILE_TIMEOUT_MS });

export class OcdProfileService {
  private static instance: OcdProfileService;

  static getInstance(): OcdProfileService {
    if (!OcdProfileService.instance) {
      OcdProfileService.instance = new OcdProfileService();
    }
    return OcdProfileService.instance;
  }

  async getOcdInfos(userId: string): Promise<OcdInfo[]> {
    const ocdProfileRef = collection(db, `user_info/${userId}/ocd_profile`);
    const ocdProfileDocs = await getDocs(ocdProfileRef);
    const ocdInfos = ocdProfileDocs.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        userProvided: data.userProvided,
        inferred: data.inferred,
        createdAt: (data.createdAt as Timestamp).toDate(),
        updatedAt: (data.updatedAt as Timestamp).toDate(),
      } as OcdInfo;
    });
    return ocdInfos;
  }

  async deleteOcdInfo(uid: string, id: string): Promise<void> {
    const ocdProfileRef = doc(db, `user_info/${uid}/ocd_profile/${id}`);
    await deleteDoc(ocdProfileRef);
  }

  async generateOcdProfile(ocdThemes: string[], questJourneyDay: number, extraGuidance: string): Promise<unknown> {
    return generateOcdProfileCall({ ocdThemes, questJourneyDay, extraGuidance });
  }
}

export const ocdProfileService = OcdProfileService.getInstance();
